import kebabCase from 'lodash/kebabCase';
import { supportedPrimaryMetrics } from 'components/CardsWrapper';
import { useFilterSearchParams } from './useFilter';
import { useMemo } from 'react';

export const useCatThreeFlavorCounts = ({
    data,
    isLoading,
    filterMissingData,
}) => {
    const shouldFilterMissingData = filterMissingData ?? true;
    const catThreeFlavorCounts = useMemo(() => {
        if (isLoading || !data?.length) return {};

        const counts = {};
        data.forEach(({ time_series: timeSeries, ...card }) => {
            const hasDefinedPrimaryMetric = supportedPrimaryMetrics.some(
                ({ key }) => card[key] || card[key] === 0,
            );
            if (shouldFilterMissingData && !hasDefinedPrimaryMetric) return;

            if (Array.isArray(card.hierarchies)) {
                const isCounted = {};
                card.hierarchies.forEach(([id, hierarchy]) => {
                    if (hierarchy?.cat1 === 'flavors') {
                        const { cat2, cat3 } = hierarchy;
                        const key = kebabCase(`${cat2}>${cat3}`);
                        if (cat2 && cat3 && !isCounted[key]) {
                            isCounted[key] = true;
                            counts[key] = (counts[key] || 0) + 1;
                        }
                    }
                });
            }
        });

        return counts;
    }, [isLoading, data?.length, supportedPrimaryMetrics]);

    return { catThreeFlavorCounts };
};

export const useCatFourFlavorCounts = ({
    data,
    isLoading,
    filterMissingData,
    handleFiltersWithoutCatFours,
    filterAccessorsAndOptions,
    catFourFilters,
}) => {
    const shouldFilterMissingData = filterMissingData ?? true;
    const skip = !catFourFilters?.length;
    const dataWithPrimaryMetric = useMemo(() => {
        if (isLoading || !data?.length) return [];
        return data.reduce((acc, { time_series: timeSeries, ...card }) => {
            const hasDefinedPrimaryMetric = supportedPrimaryMetrics.some(
                ({ key }) => card[key] || card[key] === 0,
            );
            if (shouldFilterMissingData && !hasDefinedPrimaryMetric) return acc;
            acc.push({ ...card, timeSeries });
            return acc;
        }, []);
    }, [
        isLoading,
        data?.length,
        supportedPrimaryMetrics,
        shouldFilterMissingData,
    ]);

    const { filteredData: filteredDataWithoutCatFours } = useFilterSearchParams(
        {
            data: dataWithPrimaryMetric,
            handleFilter: handleFiltersWithoutCatFours,
            initialFilterOption: {},
            filterAccessorsAndOptions,
        },
    );

    const catFourFlavorCounts = useMemo(() => {
        if (skip || !filteredDataWithoutCatFours?.length) return {};

        const counts = {};
        filteredDataWithoutCatFours.forEach(({ hierarchies }) => {
            if (!Array.isArray(hierarchies)) return;

            const isCounted = {};
            hierarchies.forEach(([id, hierarchy]) => {
                if (hierarchy?.cat1 === 'flavors') {
                    const { cat2, cat3, cat4 } = hierarchy;
                    const key = kebabCase(`${cat2}>${cat3}>${cat4}`);
                    if (cat2 && cat3 && cat4 && !isCounted[key]) {
                        isCounted[key] = true;
                        counts[key] = (counts[key] || 0) + 1;
                    }
                }
            });
        });

        return counts;
    }, [filteredDataWithoutCatFours?.length, skip]);

    return { catFourFlavorCounts };
};
